import { useEffect, useRef, useState, type ReactNode } from 'react';
import { classNames } from '../lib/utils';

interface DropdownProps {
  trigger: (props: { onClick: () => void; open: boolean }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
}

/**
 * The .dropdown-menu CSS is just an absolutely-positioned panel - it has no visibility logic of
 * its own, so every usage needs real open/close state and a click-outside handler here, or the
 * menu renders permanently visible and overlapping everything (that was the actual bug: three
 * different pages each hand-rolled a dropdown with no state at all).
 */
export function Dropdown({ trigger, children, align = 'right' }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="dropdown" style={{ display: 'inline-block' }} ref={ref}>
      {trigger({ onClick: () => setOpen((o) => !o), open })}
      {open && (
        <div className={classNames('dropdown-menu', { 'dropdown-menu-left': align === 'left' })}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
